import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import {
  useTranslation,
  useLocale,
  type Locale,
} from "./contexts/LocaleContext";
import { generateAndDownloadPdf } from "./utils/pdfGenerator";
import { authenticatedFetch } from "./config/api";
import JSZip from "jszip";
import {
  INVOICE_TEMPLATE_NAMES,
  INVOICE_STYLES,
  languages,
  LS_INVOICE_LIST_ONBOARDING_COMPLETED,
} from "./constants";
import InvoiceListTour from "./InvoiceListTour";
import { currencyNames, currencyDisplayNames } from "./currencyNames";
import { getSortedCurrencies } from "./utils/currencyLocalization";
import {
  Settings,
  ListFilter,
  Mail,
  ExternalLink,
  Clock,
  Send,
  Share2,
  Lock,
  ShieldCheck,
  X,
  HelpCircle,
  User,
  ChevronRight,
  Info,
  Globe,
  MapPin,
  FileText,
  Share,
} from "lucide-react";
import CustomDropdown from "./components/CustomDropdown";
import PaymentSuccessModal from "./components/PaymentSuccessModal";
import EmailConfirmationModal from "./components/EmailConfirmationModal";
import SettingsDialog from "./components/SettingsDialog";
import { getTemplateByStatus } from "./utils/emailTemplates";
import SaveButton from "./components/SaveButton";
import { sanitizeInput } from "./utils/sanitize";
import { getUserData } from "./utils/auth";
import Toggle from "./Toggle";

// Types
interface ActionWithTooltipProps {
  id: string;
  action: string;
  hoveredAction: { id: string; action: string } | null;
  setHoveredAction: (val: { id: string; action: string } | null) => void;
  tooltip: string;
  children: React.ReactNode;
  className?: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
}

const ActionWithTooltip: React.FC<ActionWithTooltipProps> = ({
  id,
  action,
  hoveredAction,
  setHoveredAction,
  tooltip,
  children,
  className = "",
  onClick,
  disabled = false,
}) => {
  const isHovered =
    hoveredAction?.id === id && hoveredAction?.action === action;

  return (
    <div className="relative flex items-center justify-center">
      <button
        onClick={onClick}
        onMouseEnter={() => !disabled && setHoveredAction({ id, action })}
        onMouseLeave={() => setHoveredAction(null)}
        disabled={disabled}
        className={className}
      >
        {children}
      </button>
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-slate-900 dark:bg-slate-800 text-white text-[10px] font-bold rounded-xl shadow-xl z-[60] pointer-events-none whitespace-nowrap border border-slate-700/50"
          >
            {tooltip}
            <div className="absolute top-full right-3 -mt-1 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface InvoiceListItem {
  id: string;
  internalId?: string; // Backend internal ID for PDF generation
  invoiceNo: string;
  clientName: string;
  description: string;
  amount: number;
  currency: string;
  issueDate: string;
  dueDate: string;
  status: "paid" | "pending" | "overdue";
  createdAt: string;
  downloadedAt?: string;
  clientEmail?: string;
  sellerName?: string;
  variableSymbol?: string;
  paymentDetails?: string;
}

// Mock data - в реальном приложении это будет загружаться из API
const mockInvoices: InvoiceListItem[] = [
  {
    id: "1",
    invoiceNo: "F2024-001",
    clientName: 'Společnost "Květina" s.r.o.',
    description: "Návrh a vývoj webových stránek (Design: Современный)",
    amount: 1250,
    currency: "EUR",
    issueDate: "2024-01-15",
    dueDate: "2024-01-29",
    status: "pending",
    createdAt: "2024-01-15T10:30:00Z",
    downloadedAt: "2024-01-15T11:45:00Z",
  },
  {
    id: "2",
    invoiceNo: "F2024-002",
    clientName: "Studio Nord",
    description: "Brand identity design",
    amount: 850,
    currency: "EUR",
    issueDate: "2024-01-16",
    dueDate: "2024-01-30",
    status: "paid",
    createdAt: "2024-01-16T14:20:00Z",
    downloadedAt: "2024-01-16T15:10:00Z",
  },
  {
    id: "3",
    invoiceNo: "F2024-003",
    clientName: "Anton Dev Shop",
    description: "Mobile app development",
    amount: 2100,
    currency: "EUR",
    issueDate: "2024-01-10",
    dueDate: "2024-01-24",
    status: "overdue",
    createdAt: "2024-01-10T09:15:00Z",
    downloadedAt: "2024-01-10T10:00:00Z",
  },
];

export default function InvoiceList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isBlockerActive } = useOutletContext<{
    isBlockerActive: boolean;
  }>() || {
    isBlockerActive: false,
  };
  const { locale } = useLocale();
  const t = useTranslation();

  const getLocalizedLink = (path: string) => {
    if (locale === "cs") return path;
    return `/${locale}${path}`;
  };

  // Load invoices from backend
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(
    new Set()
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | "multiple" | null>(
    null
  );
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<string | null>(
    null
  );
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Settings modal
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsDialogTab, setSettingsDialogTab] = useState<"general" | "seller">("general");
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);
  const [settings, setSettings] = useState({
    defaultDueDays: 14,
    userPreference: 0,
    customString: "",
    invoiceNumberTemplate: "YYMMDDNN",
    defaultTemplate: "Стандартный",
    invoiceStyle: "ordinary",
    defaultCurrency: "CZK",
    defaultLanguage: "cs",
    sellerName: "",
    sellerAddress: "",
    sellerTaxId: "",
    sellerDic: "",
    sellerWebsite: "",
    sellerIsVatPayer: null as boolean | null,
    sellerIsCzechSoleProprietor: false,
    sellerNotes: "",
    sellerCountry: "",
    notificationEmailHandle: "",
    notificationEmailName: "",
  });
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [customPresets, setCustomPresets] = useState<
    Array<{ name: string; value: string }>
  >([]);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(() => {
    const year = new Date().getFullYear();
    return `${year}-01-01`;
  });
  const [exportEndDate, setExportEndDate] = useState(() => {
    const year = new Date().getFullYear();
    return `${year}-12-31`;
  });
  const [exportLang, setExportLang] = useState<Locale>(locale);

  // Onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(
      LS_INVOICE_LIST_ONBOARDING_COMPLETED
    );
    const isNewRegistration = sessionStorage.getItem("new_registration") === "true";

    if (!completed || isNewRegistration) {
      setShowOnboarding(true);
      // Note: we don't remove new_registration here because the user might go to /app first or vice versa
      // It's safer to let the app page or a timeout handle it, or just keep it for the first session
    }
  }, []);

  const handleOnboardingComplete = () => {
    localStorage.setItem(LS_INVOICE_LIST_ONBOARDING_COMPLETED, "true");
    setShowOnboarding(false);
  };

  const startTour = () => {
    localStorage.removeItem(LS_INVOICE_LIST_ONBOARDING_COMPLETED);
    setShowOnboarding(true);
  };

  // Payment success modal
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // Email confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmInvoice, setConfirmInvoice] = useState<InvoiceListItem | null>(
    null
  );
  const [confirmSubject, setConfirmSubject] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [isSendingManual, setIsSendingManual] = useState<string | null>(null);
  const [hoveredAction, setHoveredAction] = useState<{
    id: string;
    action: string;
  } | null>(null);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareInvoice, setShareInvoice] = useState<InvoiceListItem | null>(
    null
  );
  const [sharePassword, setSharePassword] = useState("");
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [generatedShareUrl, setShareUrl] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && !!navigator.share) {
      setCanShare(true);
    }
  }, []);

  // Filter state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [sortBy, setSortBy] = useState<
    "issueDate" | "amount" | "dueDate" | "clientName" | "createdAt" | "status"
  >("issueDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  // Load invoices function
  const loadInvoices = async () => {
    try {
      setIsLoading(true);
      const response = await authenticatedFetch("/api/invoices-full");
      if (response.ok) {
        const fullInvoices = await response.json();

        // Convert backend format to InvoiceListItem format
        const convertedInvoices: InvoiceListItem[] = fullInvoices.map(
          (inv: any) => {
            const activeVersion = inv; // The new endpoint returns the active version directly
            if (activeVersion && activeVersion.draft) {
              const computedAmount = Array.isArray(activeVersion.draft.items)
                ? activeVersion.draft.items.reduce(
                    (sum: number, item: any) =>
                      sum + (item?.amount || 0) * (item?.quantity || 1),
                    0
                  )
                : undefined;
              return {
                id: inv.internalId,
                internalId: inv.internalId,
                invoiceNo: activeVersion.draft.invoiceNo || "",
                clientName: activeVersion.draft.buyerName || "",
                description:
                  (activeVersion.draft.items?.length > 0
                    ? activeVersion.draft.items[0]?.description
                    : "") || "",
                amount: computedAmount === 0 ? 0 : computedAmount || undefined,
                currency: activeVersion.draft.currency || "",
                issueDate: activeVersion.draft.issueDate || "",
                dueDate: activeVersion.draft.dueDate || "",
                status: (activeVersion.draft.status || "pending") as
                  | "paid"
                  | "pending"
                  | "overdue",
                createdAt: inv.createdAt || new Date().toISOString(),
                downloadedAt: activeVersion.metadata?.lastDownloadedAt,
                clientEmail: activeVersion.draft.buyerEmail || "",
                sellerName:
                  activeVersion.draft.sellerInfo?.name ||
                  activeVersion.draft.sellerName ||
                  "",
                variableSymbol:
                  activeVersion.draft.paymentInfo?.variableSymbol ||
                  activeVersion.draft.variableSymbol ||
                  "",
                paymentDetails: extractPaymentDetails(activeVersion.draft),
              } as InvoiceListItem;
            }
            return null;
          }
        );

        setInvoices(
          convertedInvoices.filter((inv) => inv !== null) as InvoiceListItem[]
        );
      } else {
        console.error("Failed to load invoices from backend");
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error loading invoices:", error);
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load settings from backend
  const loadSettings = async () => {
    try {
      const response = await authenticatedFetch("/api/settings");
      if (response.ok) {
        const data = await response.json();
        setSettings((prev) => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  const handleSaveSettings = async (
    options: { closeModals?: boolean } = {}
  ) => {
    setIsLoadingSettings(true);
    try {
      // 1. Save Due Days
      const dueDaysRes = await authenticatedFetch(
        "/api/settings/default-due-days",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            value: settings.defaultDueDays,
          }),
        }
      );

      if (!dueDaysRes.ok) {
        throw new Error("Failed to save due days");
      }

      // 2. Save Other Invoice Settings
      const invoiceSettingsRes = await authenticatedFetch(
        "/api/settings/invoice",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            invoiceNumberTemplate: settings.invoiceNumberTemplate,
            defaultTemplate: settings.defaultTemplate,
            invoiceStyle: settings.invoiceStyle,
            defaultCurrency: settings.defaultCurrency,
            defaultLanguage: settings.defaultLanguage,
            sellerName: settings.sellerName,
            sellerAddress: settings.sellerAddress,
            sellerTaxId: settings.sellerTaxId,
            sellerDic: settings.sellerDic,
            sellerWebsite: settings.sellerWebsite,
            sellerIsVatPayer: settings.sellerIsVatPayer,
            sellerIsCzechSoleProprietor: settings.sellerIsCzechSoleProprietor,
            sellerNotes: settings.sellerNotes,
            sellerCountry: settings.sellerCountry,
          }),
        }
      );

      if (!invoiceSettingsRes.ok) {
        throw new Error("Failed to save invoice settings");
      }

      await loadSettings(); // Reload settings to sync state

      if (options.closeModals) {
        setShowSettingsModal(false);
      }

      alert(t.invoiceList.settings.saved);
    } catch (error) {
      console.error("Error saving settings:", error);
      alert(
        t.common.error + ": " + (t.invoiceList.settings.title || "Settings")
      );
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Load invoices on mount and when location changes (e.g., returning from app page)
  useEffect(() => {
    loadInvoices();
    loadSettings();

    // Check for payment success from path or query param
    const handlePaymentSuccess = async () => {
      const searchParams = new URLSearchParams(location.search);
      const sessionId = searchParams.get("session_id");
      const isSuccessPath = location.pathname.includes("/payment-success");

      if (isSuccessPath || sessionId) {
        // Guard: prevent double counting within the same session
        if (sessionId) {
          const processedSessions = JSON.parse(
            sessionStorage.getItem("processed_sessions") || "[]"
          );
          if (processedSessions.includes(sessionId)) {
            console.log(
              "Session already processed, skipping modal and conversion"
            );
            // Still clean up URL but don't show modal
            window.history.replaceState(
              {},
              "",
              getLocalizedLink("/invoice-list")
            );
            return;
          }

          try {
            // Verify session with backend
            const verifyRes = await authenticatedFetch(
              `/api/stripe/verify-session?session_id=${sessionId}`
            );
            if (verifyRes.ok) {
              const verifyData = await verifyRes.json();
              if (verifyData.success) {
                setShowPaymentSuccess(true);
                // Mark session as processed
                sessionStorage.setItem(
                  "processed_sessions",
                  JSON.stringify([...processedSessions, sessionId])
                );
              }
            }
          } catch (err) {
            console.error("Error verifying payment session:", err);
          }
        } else if (isSuccessPath) {
          // If no sessionId but on success path (manual entry or legacy), show modal but warn
          setShowPaymentSuccess(true);
        }

        // Clean up the URL with a delay to allow tracking tags (Google Ads, etc.) to fire
        // 3 seconds is usually enough for most tags to process pageview/conversion events
        setTimeout(() => {
          const cleanPath = getLocalizedLink("/invoice-list");
          if (
            window.location.pathname !== cleanPath ||
            window.location.search !== ""
          ) {
            window.history.replaceState({}, "", cleanPath);
          }
        }, 3000);
      }
    };

    handlePaymentSuccess();

    // Загрузить кастомные пресеты для выпадающего списка шаблонов нумерации
    (async () => {
      try {
        const r = await authenticatedFetch("/api/invoice-templates/presets");
        if (r.ok) {
          const data = await r.json();
          setCustomPresets(data);
        }
      } catch (e) {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Keep localStorage in sync for compatibility (optional)
  useEffect(() => {
    if (invoices.length > 0) {
      localStorage.setItem("invoice-list", JSON.stringify(invoices));
    }
  }, [invoices]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusDropdownOpen &&
        !(event.target as Element).closest(".status-dropdown")
      ) {
        setStatusDropdownOpen(null);
        setDropdownPosition(null);
      }
    };

    const handleScroll = () => {
      if (statusDropdownOpen) {
        setStatusDropdownOpen(null);
        setDropdownPosition(null);
      }
    };

    const handleResize = () => {
      if (statusDropdownOpen) {
        setStatusDropdownOpen(null);
        setDropdownPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [statusDropdownOpen]);

  // Navigate to edit invoice
  const navigateToEditInvoice = async (invoice: InvoiceListItem) => {
    try {
      if (!invoice.internalId) {
        throw new Error("Invoice internalId is missing");
      }

      // Fetch full invoice data to get the complete draft including all payment options
      const response = await authenticatedFetch(
        `/api/invoices/${invoice.internalId}`
      );
      if (!response.ok) throw new Error("Failed to fetch full invoice data");

      const fullData = await response.json();
      const activeVersion = fullData.versions?.find(
        (v: any) => v.metadata.isActive
      );

      if (!activeVersion || !activeVersion.draft) {
        throw new Error("No active draft found for this invoice");
      }

      // Save full draft data to localStorage for App.tsx to pickup
      localStorage.setItem(
        "invoice-draft-v3",
        JSON.stringify(activeVersion.draft)
      );

      // Save design settings if they exist
      if (activeVersion.designSettings) {
        localStorage.setItem(
          "invoice-design-settings",
          JSON.stringify(activeVersion.designSettings)
        );
      }

      // Save internalId to localStorage
      localStorage.setItem("invoice-internal-id", invoice.internalId as string);

      // Navigate to app
      navigate(getLocalizedLink("/app"));
    } catch (error) {
      console.error("Error preparing edit mode:", error);
      alert("Не удалось загрузить полные данные инвойса для редактирования");
    }
  };

  // Update invoice status
  const updateInvoiceStatus = async (
    invoiceId: string,
    newStatus: "paid" | "pending" | "overdue"
  ) => {
    setIsUpdatingStatus(true);
    try {
      const invoice = invoices.find((inv) => inv.id === invoiceId);
      if (!invoice?.internalId) {
        console.error("Cannot update status: invoice has no internalId");
        return;
      }

      const response = await authenticatedFetch(
        `/api/invoices/${invoice.internalId}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error("Failed to update status:", error);
        alert(t.invoiceList.errors.statusChangeError);
        return;
      }

      // Update local state
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === invoiceId ? { ...inv, status: newStatus } : inv
        )
      );

      setStatusDropdownOpen(null);
      setDropdownPosition(null);
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Chyba při změně statusu");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Download archive of invoices
  const downloadInvoiceArchive = async () => {
    if (selectedInvoices.size === 0) {
      return;
    }

    setIsArchiving(true);
    try {
      // Create ZIP archive
      const zip = new JSZip();

      // Process each selected invoice
      const selectedInvoiceIds = Array.from(selectedInvoices);
      let successCount = 0;

      for (const invoiceId of selectedInvoiceIds) {
        try {
          const invoice = invoices.find((inv) => inv.id === invoiceId);
          if (!invoice?.internalId) {
            continue;
          }

          // Fetch full invoice data
          const response = await authenticatedFetch(
            `/api/invoices/${invoice.internalId}`
          );
          if (!response.ok) {
            console.error(`Failed to fetch invoice ${invoice.internalId}`);
            continue;
          }

          const fullInvoiceData = await response.json();
          const activeVersion = fullInvoiceData.versions?.find(
            (v: any) => v.metadata.isActive
          );

          if (!activeVersion) {
            continue;
          }

          const { draft, designSettings, language } = activeVersion;

          // Import translations
          const invoiceTranslations = (
            await import("./translations/invoiceTranslations")
          ).invoiceTranslations;
          const lang = (language || "en") as keyof typeof invoiceTranslations;
          const translations =
            invoiceTranslations[lang] || invoiceTranslations.en;

          // Generate filename for download
          const sanitizeFilename = (filename: string) => {
            return filename
              .toLowerCase()
              .replace(/[^a-z0-9\-_]/g, "-")
              .replace(/-+/g, "-")
              .replace(/^-|-$/g, "");
          };
          const filename = `invoice-${sanitizeFilename(draft.invoiceNo)}.pdf`;

          // Generate PDF via backend - send JSON data only (no HTML)
          // Backend generates HTML from trusted template
          const pdfResponse = await authenticatedFetch("/generate-pdf", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              draft,
              translations,
              fontFamily:
                designSettings?.fontFamily || "Inter, Arial, sans-serif",
              designSettings,
            }),
          });

          if (pdfResponse.ok) {
            const pdfBlob = await pdfResponse.blob();
            const pdfFilename = filename.replace(".pdf", "") + ".pdf";
            zip.file(pdfFilename, pdfBlob);
            successCount++;
          } else {
            let errorDetails: any = null;
            try {
              errorDetails = await pdfResponse.json();
            } catch {
              // ignore parse error
            }
            console.error(
              `Failed to generate PDF for ${invoice.internalId}`,
              errorDetails
            );
          }
        } catch (error) {
          console.error(`Error processing invoice ${invoiceId}:`, error);
        }
      }

      if (successCount === 0) {
        console.error("Failed to generate any PDFs");
        setIsArchiving(false);
        return;
      }

      // Generate and download ZIP
      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `faktury-${new Date().toISOString().split("T")[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setIsArchiving(false);
    } catch (error) {
      console.error("Error creating archive:", error);
      // Fallback: create a simple text file if backend is not available
      try {
        const invoiceData = Array.from(selectedInvoices)
          .map((id) => {
            const invoice = invoices.find((inv) => inv.id === id);
            return invoice;
          })
          .filter(Boolean);

        const content = invoiceData
          .map(
            (inv) =>
              `Faktura: ${inv?.invoiceNo}\nKlient: ${inv?.clientName}\nČástka: ${inv?.amount} ${inv?.currency}\n\n`
          )
          .join("---\n\n");

        const blob = new Blob([content], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `faktury-${new Date().toISOString().split("T")[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert(t.invoiceList.errors.backendNotAvailable);
      } catch (fallbackError) {
        console.error("Fallback error:", fallbackError);
        alert(t.invoiceList.errors.archiveError);
      }
    } finally {
      setIsArchiving(false);
    }
  };

  const handleExportToXlsx = async () => {
    try {
      setIsExporting(true);

      // Changed from /api/export/xlsx to /api/export-xlsx to avoid potential routing conflicts
      let url = `/api/export-xlsx?lang=${exportLang}`;

      if (exportStartDate) url += `&startDate=${exportStartDate}`;
      if (exportEndDate) url += `&endDate=${exportEndDate}`;

      const response = await authenticatedFetch(url);

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        const filename = `invoices-export-${
          new Date().toISOString().split("T")[0]
        }.xlsx`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        setShowExportModal(false);
      } else {
        // Handle non-OK response (like 404, 500)
        let errorMessage = "Failed to export invoices";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If response is not JSON (like a 404 HTML page)
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error("Error exporting to XLSX:", error);
      alert("An error occurred during export");
    } finally {
      setIsExporting(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("cs-CZ");
  };

  // Format amount
  const formatAmount = (amount: number, currency: string) => {
    if (currency === "CZK") {
      return `${amount.toLocaleString("cs-CZ")} Kč`;
    }
    return `${amount.toLocaleString("cs-CZ")} ${currency}`;
  };

  // Get status color and text
  const getInvoiceVariables = (invoice: InvoiceListItem | undefined) => {
    if (!invoice) return {};
    return {
      invoiceNo: invoice.invoiceNo || "{invoiceNo}",
      issueDate: invoice.issueDate || "{issueDate}",
      amount:
        invoice.amount !== undefined && invoice.amount !== null
          ? formatAmount(invoice.amount, invoice.currency)
          : "{amount}",
      paymentReference: invoice.variableSymbol || "{paymentReference}",
      paymentDetails: invoice.paymentDetails || "{paymentDetails}",
      companyName: invoice.sellerName || "{companyName}",
    };
  };

  const fillTemplateWithData = (
    template: string,
    invoice: InvoiceListItem | undefined | null
  ) => {
    const vars: any = getInvoiceVariables(invoice as any);

    return (template || "")
      .replace(/{invoiceNo}/g, vars.invoiceNo || "{invoiceNo}")
      .replace(/{issueDate}/g, vars.issueDate || "{issueDate}")
      .replace(/{amount}/g, vars.amount || "{amount}")
      .replace(
        /{paymentReference}/g,
        vars.paymentReference || "{paymentReference}"
      )
      .replace(/{paymentDetails}/g, vars.paymentDetails || "{paymentDetails}")
      .replace(/{companyName}/g, vars.companyName || "{companyName}");
  };

  const extractPaymentDetails = (draft: any) => {
    if (!draft) return "";
    const methodParts: string[] = [];

    // 1. Try paymentOptions (new format)
    if (draft.paymentOptions && Array.isArray(draft.paymentOptions)) {
      draft.paymentOptions.forEach((po: any) => {
        if (!po) return;
        if (po.type === "bank") {
          const bankParts = [];
          if (po.accountNumber) bankParts.push(po.accountNumber);
          if (po.bankName) bankParts.push(po.bankName);
          if (po.iban) bankParts.push(`IBAN: ${po.iban}`);
          if (po.bic) bankParts.push(`BIC/SWIFT: ${po.bic}`);
          if (bankParts.length > 0) methodParts.push(bankParts.join(", "));
        } else if (po.type === "link") {
          if (po.paymentUrl) methodParts.push(`Link: ${po.paymentUrl}`);
          else if (po.details) methodParts.push(po.details);
        } else if (po.details) {
          methodParts.push(po.details);
        }
      });
    }

    // 2. Try paymentInfo (fallback/old format)
    if (draft.paymentInfo) {
      const pi = draft.paymentInfo;
      const bankParts = [];
      if (pi.accountNumber) bankParts.push(pi.accountNumber);
      if (pi.bankName) bankParts.push(pi.bankName);
      if (pi.iban) bankParts.push(`IBAN: ${pi.iban}`);
      if (pi.bic) bankParts.push(`BIC/SWIFT: ${pi.bic}`);

      const infoStr = bankParts.join(", ");
      // Avoid duplication if already added from paymentOptions
      if (
        infoStr &&
        !methodParts.some((p) =>
          p.includes(pi.accountNumber || "___NON_EXISTENT___")
        )
      ) {
        methodParts.push(infoStr);
      }
    }

    // 3. Try paymentLink
    if (draft.paymentLink?.url) {
      const linkStr = `Link: ${draft.paymentLink.url}`;
      if (!methodParts.some((p) => p.includes(draft.paymentLink.url))) {
        methodParts.push(linkStr);
      }
    }

    // 4. Global VS if not in parts
    const vs = draft.paymentInfo?.variableSymbol || draft.variableSymbol;
    if (vs && !methodParts.some((p) => p.includes(vs))) {
      methodParts.push(`VS: ${vs}`);
    }

    return methodParts.filter(Boolean).join(" -- или -- ");
  };

  const handleSendManual = async (invoiceId: string) => {
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    if (!invoice) return;

    setIsSendingManual(invoice.id);

    try {
      // Fetch the full invoice data from backend
      const response = await authenticatedFetch(`/api/invoices/${invoice.id}`);
      if (response.ok) {
        const fullInvoiceData = await response.json();
        const activeVersion = fullInvoiceData.versions?.find(
          (v: any) => v.metadata.isActive
        );

        if (activeVersion && activeVersion.draft) {
          const draft = activeVersion.draft;

          // Create an enhanced invoice item with all details
          const computedAmount = Array.isArray(draft.items)
            ? draft.items.reduce(
                (sum: number, item: any) =>
                  sum + (item?.amount || 0) * (item?.quantity || 1),
                0
              )
            : invoice.amount;

          const sellerName =
            draft.sellerInfo?.name ||
            draft.sellerName ||
            draft.seller?.name ||
            invoice.sellerName ||
            "";

          const vs =
            draft.paymentInfo?.variableSymbol ||
            draft.variableSymbol ||
            invoice.variableSymbol ||
            "";

          const enhancedInvoice: InvoiceListItem = {
            ...invoice,
            amount: computedAmount,
            sellerName: sellerName,
            clientName: draft.buyerName || invoice.clientName || "",
            paymentDetails: extractPaymentDetails(draft),
            variableSymbol: vs,
          };

          // Use current locale for template
          const templateLang =
            locale === "ru" ? "ru" : locale === "cs" ? "cs" : "en";
          const template = getTemplateByStatus(invoice.status, templateLang);
          const baseSubject = template.subject;
          const baseText = template.body;

          const subject = fillTemplateWithData(baseSubject, enhancedInvoice);
          const text = fillTemplateWithData(baseText, enhancedInvoice);

          // Open confirmation modal
          setConfirmInvoice(enhancedInvoice);
          setConfirmSubject(subject);
          setConfirmText(text);
          setShowConfirmModal(true);
        }
      } else {
        console.error("Failed to load full invoice data");
        alert("Failed to load invoice data for sending");
      }
    } catch (error) {
      console.error("Error loading full invoice data:", error);
    } finally {
      setIsSendingManual(null);
    }
  };

  const confirmSendManual = async (
    to: string,
    finalSubject: string,
    finalText: string
  ) => {
    if (!confirmInvoice) return;

    setIsSendingManual(confirmInvoice.id);
    setShowConfirmModal(false);

    try {
      const response = await authenticatedFetch("/api/send-reminder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: to || confirmInvoice.clientEmail,
          subject: finalSubject,
          text: finalText,
          invoiceId: confirmInvoice.internalId,
        }),
      });

      if (response.ok) {
        alert(
          locale === "ru"
            ? "Напоминание успешно отправлено!"
            : locale === "cs"
            ? "Připomenutí bylo úspěšně odesláno!"
            : "Reminder sent successfully!"
        );
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send reminder");
      }
    } catch (e: any) {
      console.error(e);
      alert(
        locale === "ru"
          ? "Ошибка при отправке напоминания"
          : locale === "cs"
          ? "Chyba při odesílání připomenutí"
          : "Error sending reminder"
      );
    } finally {
      setIsSendingManual(null);
      setConfirmInvoice(null);
    }
  };

  const getStatusInfo = (status: InvoiceListItem["status"]) => {
    switch (status) {
      case "paid":
        return {
          color:
            "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
          text: t.invoiceList.status.paid,
        };
      case "pending":
        return {
          color:
            "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
          text: t.invoiceList.status.pending,
        };
      case "overdue":
        return {
          color:
            "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
          text: t.invoiceList.status.overdue,
        };
      default:
        return {
          color:
            "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
          text: "Unknown",
        };
    }
  };

  // Search and filter functionality
  const filteredInvoices = useMemo(() => {
    let result = invoices;

    // Apply status filter
    if (filterStatus !== "all") {
      result = result.filter((invoice) => invoice.status === filterStatus);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((invoice) => {
        const dateStr = invoice.issueDate ? formatDate(invoice.issueDate) : "";
        const dueDateStr = invoice.dueDate ? formatDate(invoice.dueDate) : "";
        const statusStr = getStatusInfo(invoice.status).text.toLowerCase();

        return (
          invoice.invoiceNo.toLowerCase().includes(query) ||
          invoice.clientName.toLowerCase().includes(query) ||
          invoice.description.toLowerCase().includes(query) ||
          invoice.amount.toString().includes(query) ||
          dateStr.includes(query) ||
          dueDateStr.includes(query) ||
          statusStr.includes(query)
        );
      });
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "issueDate":
          comparison =
            new Date(a.issueDate || 0).getTime() -
            new Date(b.issueDate || 0).getTime();
          break;
        case "amount":
          comparison = (a.amount || 0) - (b.amount || 0);
          break;
        case "dueDate":
          comparison =
            new Date(a.dueDate || 0).getTime() -
            new Date(b.dueDate || 0).getTime();
          break;
        case "clientName":
          comparison = (a.clientName || "").localeCompare(b.clientName || "");
          break;
        case "createdAt":
          comparison =
            new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime();
          break;
        case "status":
          comparison = (a.status || "").localeCompare(b.status || "");
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [invoices, searchQuery, sortBy, sortOrder]);

  // Handle individual selection
  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedInvoices(newSelected);
  };

  // Handle select all
  const toggleSelectAll = () => {
    if (selectedInvoices.size === filteredInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredInvoices.map((inv) => inv.id)));
    }
  };

  // Delete functionality
  const handleDelete = async (id: string | "multiple") => {
    try {
      if (id === "multiple") {
        // Prepare internal IDs for batch delete
        const internalIds = Array.from(selectedInvoices)
          .map(
            (invoiceId) =>
              invoices.find((inv) => inv.id === invoiceId)?.internalId
          )
          .filter(Boolean) as string[];

        if (internalIds.length > 0) {
          const BATCH_SIZE = 100;
          for (let i = 0; i < internalIds.length; i += BATCH_SIZE) {
            const batch = internalIds.slice(i, i + BATCH_SIZE);
            const response = await authenticatedFetch(
              "/api/invoices/batch-delete",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ internalIds: batch }),
              }
            );

            if (!response.ok) {
              throw new Error(`Batch delete failed at index ${i}`);
            }
          }
        }
      } else {
        // Delete single invoice from backend
        const invoice = invoices.find((inv) => inv.id === id);
        if (invoice?.internalId) {
          const response = await authenticatedFetch(
            `/api/invoices/${invoice.internalId}`,
            {
              method: "DELETE",
            }
          );
          if (!response.ok) {
            console.error("Failed to delete invoice from backend");
            alert(t.invoiceList.errors.backendDeleteError);
            return;
          }
        }
      }

      // Reload invoices from backend after deletion
      await loadInvoices();

      // Clear selection
      setSelectedInvoices(new Set());
    } catch (error) {
      console.error("Error deleting invoice(s):", error);
      alert(t.invoiceList.errors.deleteError);
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  // Duplicate functionality
  const handleDuplicate = async (invoice: InvoiceListItem) => {
    try {
      // Fetch full invoice data
      if (!invoice.internalId) {
        console.error("Cannot duplicate: invoice has no internalId");
        return;
      }

      const response = await authenticatedFetch(
        `/api/invoices/${invoice.internalId}`
      );
      if (!response.ok) {
        console.error("Cannot load invoice data for duplication");
        return;
      }

      const fullInvoiceData = await response.json();
      const activeVersion = fullInvoiceData.versions?.find(
        (v: any) => v.metadata.isActive
      );

      if (!activeVersion) {
        console.error("Cannot duplicate: no active version");
        return;
      }

      // Keep original invoiceNo (don't add -COPY)
      const draftCopy = {
        ...activeVersion.draft,
        invoiceNo: activeVersion.draft.invoiceNo,
      };

      // Save as new invoice (will get new internalId)
      const saveResponse = await authenticatedFetch("/api/invoices/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          internalId: undefined, // Let backend generate new ID
          draft: draftCopy,
          designSettings: activeVersion.designSettings,
          language: activeVersion.language,
        }),
      });

      if (saveResponse.ok) {
        // Reload invoices
        await loadInvoices();
      } else {
        console.error("Failed to duplicate invoice");
      }
    } catch (error) {
      console.error("Error duplicating invoice:", error);
    }
  };

  const handleDuplicateMultiple = async () => {
    try {
      const selectedIds = Array.from(selectedInvoices);

      for (const id of selectedIds) {
        try {
          const invoice = invoices.find((inv) => inv.id === id);
          if (!invoice?.internalId) {
            continue;
          }

          // Fetch full invoice data
          const response = await authenticatedFetch(
            `/api/invoices/${invoice.internalId}`
          );
          if (!response.ok) {
            continue;
          }

          const fullInvoiceData = await response.json();
          const activeVersion = fullInvoiceData.versions?.find(
            (v: any) => v.metadata.isActive
          );

          if (!activeVersion) {
            continue;
          }

          // Keep original invoiceNo (don't add -COPY)
          const draftCopy = {
            ...activeVersion.draft,
            invoiceNo: activeVersion.draft.invoiceNo,
          };

          // Save as new invoice
          await authenticatedFetch("/api/invoices/save", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              internalId: undefined, // Let backend generate new ID
              draft: draftCopy,
              designSettings: activeVersion.designSettings,
              language: activeVersion.language,
            }),
          });
        } catch (error) {
          console.error(`Error duplicating invoice ${id}:`, error);
        }
      }

      // Reload invoices
      await loadInvoices();
      setSelectedInvoices(new Set());
    } catch (error) {
      console.error("Error duplicating invoices:", error);
    }
  };

  const handleGenerateShare = async () => {
    if (!shareInvoice || !sharePassword) return;
    setIsGeneratingShare(true);
    try {
      const response = await authenticatedFetch(
        `/api/invoices/${shareInvoice.id}/share`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            password: sharePassword,
            expiresDays: 30, // Default 30 days
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${window.location.origin}${data.url}`;
        setShareUrl(fullUrl);
      } else {
        alert(
          locale === "ru"
            ? "Ошибка при создании ссылки"
            : "Error creating share link"
        );
      }
    } catch (error) {
      console.error("Error creating share link:", error);
    } finally {
      setIsGeneratingShare(false);
    }
  };

  return (
    <div className="">
      {/* Header */}
      <header className="z-40 backdrop-blur-md bg-white/80 dark:bg-slate-900/80 border border-slate-200/50 dark:border-slate-700/50 shadow-sm rounded-md transition-colors">
        <div className="mx-auto max-w-7xl px-4 py-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center flex-wrap gap-2">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight whitespace-nowrap transition-colors">
              {t.invoiceList.title}
            </h1>

            <button
              id="tour-restart-button"
              onClick={startTour}
              className="px-3 py-1.5 border-2 border-sky-400 text-sky-600 hover:bg-sky-50 dark:border-sky-500 dark:text-sky-400 dark:hover:bg-sky-900/20 rounded-xl transition-all flex items-center gap-1.5 shadow-sm font-bold text-sm"
              title={t.invoiceList.onboarding.tutorialButton}
            >
              <HelpCircle className="h-5 w-5" />
              <span className="whitespace-nowrap">
                {t.invoiceList.onboarding.tutorialButton}
              </span>
            </button>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <motion.button
              id="tour-invoice-settings"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowSettingsModal(true)}
              className="px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 shadow-sm flex items-center gap-2"
              title={t.invoiceList.settings.title}
            >
              <span className="text-sm font-semibold">
                {t.invoiceList.settings.title}
              </span>
              <Settings className="h-5 w-5" />
            </motion.button>
            <motion.button
              id="tour-invoice-new"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                try {
                  localStorage.removeItem("invoice-draft-v3");
                  localStorage.removeItem("invoice-internal-id");
                  localStorage.setItem("skip-invoice-check", "true");
                } catch {}
                navigate(getLocalizedLink("/app"));
              }}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-pink-600 to-purple-600 text-white hover:from-pink-500 hover:to-purple-500 font-semibold text-sm whitespace-nowrap transition-all duration-200 shadow-lg border-none"
            >
              + {t.invoiceList.newInvoice}
            </motion.button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="rounded-full h-10 w-10 border-[3px] border-slate-200 dark:border-slate-700 border-t-purple-600"
            />
          </div>
        )}

        {/* Search and Actions */}
        {!isLoading && (
          <div className="mb-8 space-y-6">
            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="relative"
            >
              <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 dark:text-slate-500 z-10" />
              <input
                type="text"
                placeholder={t.invoiceList.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(sanitizeInput(e.target.value))}
                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 text-sm placeholder-slate-400 dark:placeholder-slate-500 shadow-sm hover:shadow-md transition-all duration-200"
              />
            </motion.div>

            {/* Action Bar */}
            <motion.div
              id="tour-invoice-bulk"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="flex items-center justify-between gap-4 flex-wrap py-2"
            >
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2.5 cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={
                      selectedInvoices.size === filteredInvoices.length &&
                      filteredInvoices.length > 0
                    }
                    onChange={toggleSelectAll}
                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 focus:ring-2 w-4 h-4"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors">
                    {t.common.select} {t.common.all}
                  </span>
                </label>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-purple-600 dark:text-purple-400 font-semibold whitespace-nowrap px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 rounded-lg transition-colors">
                    {selectedInvoices.size} {t.common.selected}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDuplicateMultiple}
                    disabled={selectedInvoices.size === 0}
                    className="px-4 py-1.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-all duration-200 shadow-md"
                  >
                    {t.common.duplicate}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setDeleteTarget("multiple");
                      setShowDeleteModal(true);
                    }}
                    disabled={selectedInvoices.size === 0}
                    className="px-4 py-1.5 rounded-xl bg-red-600 text-white hover:bg-red-700 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-all duration-200 shadow-md"
                  >
                    {t.common.delete}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={downloadInvoiceArchive}
                    disabled={selectedInvoices.size === 0 || isArchiving}
                    className="px-4 py-1.5 rounded-xl bg-green-600 text-white hover:bg-green-700 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap transition-all duration-200 shadow-md"
                  >
                    <DownloadIcon className="h-4 w-4" />
                    {t.invoiceList.archive.download}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setExportLang(locale);
                      setShowExportModal(true);
                    }}
                    disabled={isExporting}
                    className="px-4 py-1.5 rounded-xl bg-slate-600 text-white hover:bg-slate-700 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap transition-all duration-200 shadow-md"
                  >
                    {isExporting ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      />
                    ) : (
                      <TableIcon className="h-4 w-4" />
                    )}
                    {t.statistics.exportToExcel}
                  </motion.button>
                  {isArchiving && (
                    <div className="flex items-center gap-2 ml-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="rounded-full h-5 w-5 border-2 border-green-600 border-t-transparent"
                      />
                      <span className="text-sm text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">
                        {t.invoiceList.archive.archiving}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <motion.button
                  id="tour-invoice-filters"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowFilterModal(true)}
                  className="px-4 py-2 rounded-xl bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 font-semibold text-sm whitespace-nowrap transition-all duration-200 shadow-sm flex items-center gap-2"
                >
                  <ListFilter className="h-4 w-4" />
                  {t.invoiceList.filter || "Filter"}
                </motion.button>
                <span className="text-sm text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap transition-colors">
                  {t.common.showing}{" "}
                  <span className="text-slate-700 dark:text-slate-300 font-semibold">
                    {filteredInvoices.length}
                  </span>{" "}
                  {t.common.of}{" "}
                  <span className="text-slate-700 dark:text-slate-300 font-semibold">
                    {invoices.length}
                  </span>
                </span>
              </div>
            </motion.div>
          </div>
        )}

        {/* Invoices Table */}
        {!isLoading && (
          <motion.div
            id="tour-invoice-table"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-xl overflow-hidden transition-colors"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: "900px" }}>
                <thead className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 transition-colors">
                  <tr>
                    <th className="px-2 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle w-10"></th>
                    <th className="px-2 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle w-64 max-md:w-52">
                      {t.invoiceList.columns.invoiceNumber}
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle">
                      {t.invoiceList.columns.client}
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle">
                      {t.invoiceList.columns.description}
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle">
                      {t.invoiceList.columns.amount}
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle">
                      {t.invoiceList.columns.status}
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle">
                      {t.invoiceList.columns.issueDate}
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle">
                      {t.invoiceList.columns.dueDate}
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-tight align-middle">
                      {t.invoiceList.columns.actions}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                  <AnimatePresence>
                    {filteredInvoices.map((invoice, idx) => (
                      <motion.tr
                        key={invoice.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group"
                      >
                        <td className="px-2 py-2 text-center align-middle">
                          <input
                            type="checkbox"
                            checked={selectedInvoices.has(invoice.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSelection(invoice.id);
                            }}
                            className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 focus:ring-2"
                          />
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToEditInvoice(invoice);
                            }}
                            className="flex items-center justify-between gap-1 text-left font-semibold text-slate-900 dark:text-slate-100 hover:text-purple-600 dark:hover:text-purple-400 transition-colors group/btn text-xs w-full"
                            title={t.invoiceList.actions.edit}
                          >
                            <span
                              className="whitespace-nowrap truncate flex-1"
                              title={invoice.invoiceNo}
                            >
                              {invoice.invoiceNo}
                            </span>
                            <ChevronRightIcon className="w-3 h-3 text-slate-400 dark:text-slate-500 group-hover/btn:text-purple-600 dark:group-hover/btn:text-purple-400 group-hover/btn:translate-x-0.5 transition-all flex-shrink-0" />
                          </button>
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <div
                            className="text-slate-900 dark:text-slate-100 whitespace-nowrap truncate max-w-32"
                            title={invoice.clientName || ""}
                          >
                            {invoice.clientName || ""}
                          </div>
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <div
                            className="max-w-40 truncate text-slate-700 dark:text-slate-300"
                            title={invoice.description || ""}
                          >
                            {invoice.description || ""}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right align-middle">
                          <div className="font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                            {invoice.amount != null
                              ? formatAmount(invoice.amount, invoice.currency)
                              : ""}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center align-middle">
                          <div className="relative inline-block status-dropdown">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                setDropdownPosition({
                                  top: rect.bottom + 4,
                                  left: rect.left,
                                });
                                setStatusDropdownOpen(
                                  statusDropdownOpen === invoice.id
                                    ? null
                                    : invoice.id
                                );
                              }}
                              disabled={isUpdatingStatus}
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity ${
                                getStatusInfo(invoice.status).color
                              } ${
                                isUpdatingStatus
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              title={t.invoiceList.actions.changeStatus}
                            >
                              {getStatusInfo(invoice.status).text}
                              <ChevronDownIcon className="w-3 h-3 ml-1" />
                            </button>

                            {statusDropdownOpen === invoice.id &&
                              dropdownPosition && (
                                <div
                                  className="fixed bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl z-[100]"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                    width: "max-content",
                                  }}
                                >
                                  <div className="py-1">
                                    {(
                                      ["pending", "paid", "overdue"] as const
                                    ).map((status) => (
                                      <button
                                        key={status}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateInvoiceStatus(
                                            invoice.id,
                                            status
                                          );
                                        }}
                                        className="w-full text-left px-2 py-2 text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center"
                                      >
                                        <span
                                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${
                                            getStatusInfo(status).color
                                          }`}
                                        >
                                          {getStatusInfo(status).text}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <div className="text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                            {invoice.issueDate
                              ? formatDate(invoice.issueDate)
                              : ""}
                          </div>
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <div className="text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                            {invoice.dueDate ? formatDate(invoice.dueDate) : ""}
                          </div>
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <div
                            id={idx === 0 ? "tour-invoice-actions" : undefined}
                            className="flex items-center justify-center gap-1"
                          >
                            {/* 1. Save (Download) */}
                            <ActionWithTooltip
                              id={invoice.id}
                              action="download"
                              hoveredAction={hoveredAction}
                              setHoveredAction={setHoveredAction}
                              tooltip={t.invoiceList.actions.downloadPdf}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  let internalId = invoice.internalId;

                                  // If no internalId, try to find it from backend by invoice number
                                  if (!internalId) {
                                    try {
                                      const allInvoices =
                                        await authenticatedFetch(
                                          "/api/invoices"
                                        );
                                      if (allInvoices.ok) {
                                        const invoicesList =
                                          await allInvoices.json();
                                        // Try to find by invoice number in any version
                                        for (const inv of invoicesList) {
                                          const fullResponse =
                                            await authenticatedFetch(
                                              `/api/invoices/${inv.internalId}`
                                            );
                                          if (fullResponse.ok) {
                                            const fullData =
                                              await fullResponse.json();
                                            const version =
                                              fullData.versions?.find(
                                                (v: any) =>
                                                  v.draft?.invoiceNo ===
                                                  invoice.invoiceNo
                                              );
                                            if (version) {
                                              const foundInternalId =
                                                inv.internalId;
                                              internalId = foundInternalId;
                                              // Update the invoice in state
                                              setInvoices(
                                                (prev: InvoiceListItem[]) =>
                                                  prev.map(
                                                    (inv: InvoiceListItem) =>
                                                      inv.id === invoice.id
                                                        ? {
                                                            ...inv,
                                                            internalId:
                                                              foundInternalId,
                                                          }
                                                        : inv
                                                  )
                                              );
                                              break;
                                            }
                                          }
                                        }
                                      }
                                    } catch (e) {
                                      console.error(
                                        "Error finding internalId:",
                                        e
                                      );
                                    }
                                  }

                                  if (internalId) {
                                    // Fetch the full invoice data from backend
                                    const response = await authenticatedFetch(
                                      `/api/invoices/${internalId}`
                                    );
                                    if (response.ok) {
                                      const fullInvoiceData =
                                        await response.json();
                                      const activeVersion =
                                        fullInvoiceData.versions?.find(
                                          (v: any) => v.metadata.isActive
                                        );
                                      if (activeVersion) {
                                        const {
                                          draft,
                                          designSettings,
                                          language,
                                        } = activeVersion;

                                        // Import translations
                                        const invoiceTranslations = (
                                          await import(
                                            "./translations/invoiceTranslations"
                                          )
                                        ).invoiceTranslations;
                                        const lang = (language ||
                                          "en") as keyof typeof invoiceTranslations;
                                        const translations =
                                          invoiceTranslations[lang] ||
                                          invoiceTranslations.en;

                                        // Generate PDF
                                        await generateAndDownloadPdf({
                                          draft,
                                          translations,
                                          designSettings,
                                          fontFamily:
                                            designSettings?.fontFamily ||
                                            "Inter, Arial, sans-serif",
                                        });
                                      } else {
                                        throw new Error(
                                          "No active version found"
                                        );
                                      }
                                    } else {
                                      throw new Error(
                                        "Failed to fetch invoice data"
                                      );
                                    }
                                  } else {
                                    alert(
                                      t.common.error + ": Invoice ID not found"
                                    );
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error downloading invoice:",
                                    error
                                  );
                                  alert(
                                    `${t.common.error}: ${
                                      error instanceof Error
                                        ? error.message
                                        : "Unknown error"
                                    }`
                                  );
                                }
                              }}
                              className="p-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors shadow-sm"
                            >
                              <DownloadIcon className="h-3.5 w-3.5" />
                            </ActionWithTooltip>

                            {/* 2. Share */}
                            <ActionWithTooltip
                              id={invoice.id}
                              action="share"
                              hoveredAction={hoveredAction}
                              setHoveredAction={setHoveredAction}
                              tooltip={t.common.shareLink}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShareInvoice(invoice);
                                setShareUrl(null);
                                setSharePassword("");
                                setLinkCopied(false);
                                setShowShareModal(true);
                              }}
                              className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors shadow-sm"
                            >
                              <Share2 className="h-3.5 w-3.5" />
                            </ActionWithTooltip>

                            {/* 3. Email (Send) */}
                            <ActionWithTooltip
                              id={invoice.id}
                              action="send"
                              hoveredAction={hoveredAction}
                              setHoveredAction={setHoveredAction}
                              tooltip={t.clientNotifications.sendManual}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendManual(invoice.id);
                              }}
                              disabled={isSendingManual === invoice.id}
                              className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors shadow-sm"
                            >
                              {isSendingManual === invoice.id ? (
                                <Clock className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Mail className="h-3.5 w-3.5" />
                              )}
                            </ActionWithTooltip>

                            {/* 4. Duplicate */}
                            <ActionWithTooltip
                              id={invoice.id}
                              action="duplicate"
                              hoveredAction={hoveredAction}
                              setHoveredAction={setHoveredAction}
                              tooltip={t.common.duplicate}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicate(invoice);
                              }}
                              className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-sm"
                            >
                              <CopyIcon className="h-3.5 w-3.5" />
                            </ActionWithTooltip>

                            {/* 5. Delete */}
                            <ActionWithTooltip
                              id={invoice.id}
                              action="delete"
                              hoveredAction={hoveredAction}
                              setHoveredAction={setHoveredAction}
                              tooltip={t.common.delete}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(invoice.id);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors shadow-sm"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </ActionWithTooltip>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {!isLoading && filteredInvoices.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center py-20"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="mx-auto w-32 h-32 flex items-center justify-center mb-8"
            >
              <img
                src="/invotio_pointing_down.png"
                alt="Invoitio"
                className="w-full h-full object-contain"
              />
            </motion.div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6 transition-colors">
              {searchQuery
                ? t.common.noInvoicesFound
                : t.invoiceList.noInvoices}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-8 text-lg transition-colors">
              {searchQuery ? t.common.tryChangingQuery : ""}
            </p>
            {!searchQuery && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(getLocalizedLink("/app"))}
                className="px-8 py-4 rounded-2xl bg-gradient-to-r from-pink-600 to-purple-600 text-white hover:from-pink-500 hover:to-purple-500 font-semibold text-base shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40 transition-all duration-200"
              >
                {t.common.createFirstInvoice}
              </motion.button>
            )}
          </motion.div>
        )}
      </div>

      {/* Settings Modal */}
      <AnimatePresence>
        <SettingsDialog
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          showSellerSettings={settingsDialogTab === "seller"}
          onSellerSettingsOpen={() => setSettingsDialogTab("seller")}
        />
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[200]"
            onMouseDown={(e) =>
              setMouseDownOnOverlay(e.target === e.currentTarget)
            }
            onMouseUp={(e) => {
              if (mouseDownOnOverlay && e.target === e.currentTarget) {
                setShowDeleteModal(false);
              }
              setMouseDownOnOverlay(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md mx-4 shadow-2xl border border-slate-200/50 dark:border-slate-700/50 transition-colors"
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-pink-100 dark:from-red-900/30 dark:to-pink-900/30 rounded-xl flex items-center justify-center shadow-sm">
                  <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 transition-colors">
                  {deleteTarget === "multiple"
                    ? t.invoiceList.deleteModal.title
                    : t.invoiceList.deleteModal.title}
                </h3>
              </div>

              <p className="text-slate-600 dark:text-slate-400 mb-8 text-base leading-relaxed transition-colors">
                {deleteTarget === "multiple"
                  ? t.invoiceList.deleteModal.messageMultiple.replace(
                      "{count}",
                      selectedInvoices.size.toString()
                    )
                  : t.invoiceList.deleteModal.message}
              </p>

              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 font-semibold transition-all duration-200 shadow-sm"
                >
                  {t.invoiceList.deleteModal.cancel}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => deleteTarget && handleDelete(deleteTarget)}
                  className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white hover:from-red-500 hover:to-pink-500 font-semibold transition-all duration-200 shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40"
                >
                  {t.invoiceList.deleteModal.confirm}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter Modal */}
      <AnimatePresence>
        {showFilterModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[200]"
            onMouseDown={(e) =>
              setMouseDownOnOverlay(e.target === e.currentTarget)
            }
            onMouseUp={(e) => {
              if (mouseDownOnOverlay && e.target === e.currentTarget) {
                setShowFilterModal(false);
              }
              setMouseDownOnOverlay(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-slate-800 rounded-3xl p-6 max-w-md mx-4 shadow-2xl border border-slate-200/50 dark:border-slate-700/50 transition-colors"
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl flex items-center justify-center shadow-sm">
                  <ListFilter className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 transition-colors">
                  {t.invoiceList.filter || "Filter"}
                </h3>
              </div>

              <div className="space-y-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 transition-colors">
                    {t.invoiceList.filterStatus || "Status"}
                  </label>
                  <CustomDropdown
                    options={[
                      { value: "all", label: t.common.all || "All" },
                      { value: "paid", label: t.invoiceList.status.paid },
                      { value: "pending", label: t.invoiceList.status.pending },
                      { value: "overdue", label: t.invoiceList.status.overdue },
                    ]}
                    value={filterStatus}
                    onChange={(value) => setFilterStatus(value)}
                    placeholder={t.common.select || "Select"}
                  />
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 transition-colors">
                    {t.invoiceList.filterSortBy || "Sort by"}
                  </label>
                  <CustomDropdown
                    options={[
                      {
                        value: "issueDate",
                        label: t.invoiceList.filterIssueDate || "Issue Date",
                      },
                      {
                        value: "amount",
                        label: t.invoiceList.filterAmount || "Amount",
                      },
                      {
                        value: "dueDate",
                        label: t.invoiceList.filterDueDate || "Payment Date",
                      },
                      {
                        value: "clientName",
                        label: t.invoiceList.filterClientName || "Client Name",
                      },
                      {
                        value: "createdAt",
                        label: t.invoiceList.filterCreatedAt || "Creation Date",
                      },
                      {
                        value: "status",
                        label: t.invoiceList.filterStatusSort || "By Status",
                      },
                    ]}
                    value={sortBy}
                    onChange={(value) => setSortBy(value as typeof sortBy)}
                    placeholder={
                      t.invoiceList.filterSelectField || "Select field"
                    }
                  />
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 transition-colors">
                    {t.invoiceList.filterSortOrder || "Sort order"}
                  </label>
                  <CustomDropdown
                    options={[
                      {
                        value: "desc",
                        label: t.invoiceList.filterDescending || "Descending",
                      },
                      {
                        value: "asc",
                        label: t.invoiceList.filterAscending || "Ascending",
                      },
                    ]}
                    value={sortOrder}
                    onChange={(value) =>
                      setSortOrder(value as typeof sortOrder)
                    }
                    placeholder={
                      t.invoiceList.filterSelectOrder || "Select order"
                    }
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSortBy("issueDate");
                    setSortOrder("desc");
                    setFilterStatus("all");
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 font-semibold text-sm transition-all duration-200 shadow-sm"
                >
                  {t.invoiceList.filterReset || "Reset"}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowFilterModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-pink-600 to-purple-600 text-white hover:from-pink-500 hover:to-purple-500 font-semibold text-sm transition-all duration-200 shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40"
                >
                  {t.common.apply || "Apply"}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PaymentSuccessModal
        isOpen={showPaymentSuccess}
        onClose={() => setShowPaymentSuccess(false)}
      />

      <EmailConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmInvoice(null);
        }}
        invoice={confirmInvoice}
        initialSubject={confirmSubject}
        initialText={confirmText}
        onSend={confirmSendManual}
        t={t}
        locale={locale}
        tagColor="yellow"
        type="manual"
        isEmailConfigured={
          !!(settings.notificationEmailHandle && settings.notificationEmailName)
        }
      />

      {/* Share Invoice Modal */}
      <AnimatePresence>
        {showShareModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[200] p-4"
            onMouseDown={(e) =>
              setMouseDownOnOverlay(e.target === e.currentTarget)
            }
            onMouseUp={(e) => {
              if (mouseDownOnOverlay && e.target === e.currentTarget) {
                setShowShareModal(false);
                setLinkCopied(false);
              }
              setMouseDownOnOverlay(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800"
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
                <h2 className="text-lg sm:text-xl font-black text-slate-900 dark:text-slate-100 uppercase tracking-tight">
                  {locale === "ru"
                    ? "Поделиться счетом"
                    : locale === "cs"
                    ? "Sdílet fakturu"
                    : "Share Invoice"}
                </h2>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setLinkCopied(false);
                  }}
                  className="p-1.5 sm:p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 text-center overflow-y-auto custom-scrollbar">
                {!generatedShareUrl ? (
                  <>
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto text-emerald-600 dark:text-emerald-400 shadow-inner shrink-0">
                      <Lock size={32} className="sm:w-10 sm:h-10" />
                    </div>
                    <div>
                      <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 font-medium mb-4 leading-snug">
                        {locale === "ru"
                          ? "Установите пароль для просмотра этого счета. Только люди с паролем смогут увидеть PDF."
                          : locale === "cs"
                          ? "Nastavte heslo pro zobrazení této faktury. Pouze lidé s heslem uvidí PDF."
                          : "Set a password to view this invoice. Only people with the password will see the PDF."}
                      </p>
                      <input
                        type="password"
                        placeholder={
                          locale === "ru"
                            ? "Введите пароль"
                            : locale === "cs"
                            ? "Zadejte heslo"
                            : "Enter password"
                        }
                        value={sharePassword}
                        onChange={(e) => setSharePassword(e.target.value)}
                        className="w-full px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500/50 outline-none text-center font-bold text-sm sm:text-base"
                      />
                    </div>
                    <button
                      onClick={handleGenerateShare}
                      disabled={!sharePassword || isGeneratingShare}
                      className="w-full py-3.5 sm:py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl sm:rounded-2xl font-black shadow-lg shadow-emerald-500/25 transition-all disabled:opacity-50 text-sm sm:text-base"
                    >
                      {isGeneratingShare ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                      ) : locale === "ru" ? (
                        "Создать защищенную ссылку"
                      ) : locale === "cs" ? (
                        "Vytvořit zabezpečený odkaz"
                      ) : (
                        "Create secure link"
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 dark:bg-blue-900/30 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto text-blue-600 dark:text-blue-400 shadow-inner shrink-0">
                      <ShieldCheck size={32} className="sm:w-10 sm:h-10" />
                    </div>
                    <div className="space-y-4 sm:space-y-6">
                      <p className="text-emerald-600 dark:text-emerald-400 font-black text-sm sm:text-base">
                        {locale === "ru"
                          ? "Ссылка создана!"
                          : locale === "cs"
                          ? "Odkaz vytvořen!"
                          : "Link created!"}
                      </p>

                      <div className="space-y-3">
                        <div className="text-left">
                          <label className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">
                            {locale === "ru"
                              ? "Ссылка на счет"
                              : locale === "cs"
                              ? "Odkaz na fakturu"
                              : "Invoice Link"}
                          </label>
                          <div className="relative group">
                            <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-800 rounded-xl sm:rounded-2xl border border-slate-100 dark:border-slate-700 break-all text-[10px] sm:text-xs font-mono text-slate-600 dark:text-slate-400 pr-10 sm:pr-12">
                              {generatedShareUrl}
                            </div>
                            <button
                              onClick={() => {
                                if (generatedShareUrl) {
                                  navigator.clipboard.writeText(
                                    generatedShareUrl
                                  );
                                  setUrlCopied(true);
                                  setTimeout(() => setUrlCopied(false), 2000);
                                }
                              }}
                              className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg sm:rounded-xl transition-colors text-slate-400 hover:text-indigo-600"
                            >
                              {urlCopied ? (
                                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                              ) : (
                                <CopyIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="text-left">
                          <label className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">
                            {t.landing.auth.password}
                          </label>
                          <div className="relative group">
                            <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-800 rounded-xl sm:rounded-2xl border border-slate-100 dark:border-slate-700 break-all text-[10px] sm:text-xs font-mono text-slate-600 dark:text-slate-400 pr-10 sm:pr-12">
                              {sharePassword}
                            </div>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(sharePassword);
                                setPasswordCopied(true);
                                setTimeout(
                                  () => setPasswordCopied(false),
                                  2000
                                );
                              }}
                              className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg sm:rounded-xl transition-colors text-slate-400 hover:text-indigo-600"
                            >
                              {passwordCopied ? (
                                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                              ) : (
                                <CopyIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2.5 sm:gap-3 pt-1 sm:pt-2">
                        <button
                          onClick={() => {
                            const fullText = `${generatedShareUrl}\nPassword: ${sharePassword}`;
                            navigator.clipboard.writeText(fullText);
                            setLinkCopied(true);
                            setTimeout(() => setLinkCopied(false), 2000);
                          }}
                          className={`w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl font-black text-xs sm:text-sm transition-all flex items-center justify-center gap-2 ${
                            linkCopied
                              ? "bg-emerald-500 text-white shadow-emerald-500/20"
                              : "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900"
                          }`}
                        >
                          {linkCopied ? (
                            <>
                              <ShieldCheck className="w-4 h-4" />
                              {locale === "ru"
                                ? "Скопировано!"
                                : locale === "cs"
                                ? "Zkopírováno!"
                                : "Copied!"}
                            </>
                          ) : locale === "ru" ? (
                            "Скопировать ссылку и пароль"
                          ) : locale === "cs" ? (
                            "Kopírovat odkaz a heslo"
                          ) : (
                            "Copy link and password"
                          )}
                        </button>

                        <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                          <a
                            href={`https://wa.me/?text=${encodeURIComponent(
                              `${generatedShareUrl}\nPassword: ${sharePassword}`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-3 sm:px-4 bg-[#25D366] hover:bg-[#20ba56] text-white rounded-xl sm:rounded-2xl font-bold text-[10px] sm:text-xs transition-all shadow-md"
                          >
                            <svg
                              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.353-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.87 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                            {locale === "ru"
                              ? "WhatsApp"
                              : locale === "cs"
                              ? "WhatsApp"
                              : "WhatsApp"}
                          </a>
                          <a
                            href={`https://t.me/share/url?url=${encodeURIComponent(
                              generatedShareUrl
                            )}&text=${encodeURIComponent(
                              `Password: ${sharePassword}`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-3 sm:px-4 bg-[#0088cc] hover:bg-[#0077b5] text-white rounded-xl sm:rounded-2xl font-bold text-[10px] sm:text-xs transition-all shadow-md"
                          >
                            <svg
                              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.63.15-.16 2.72-2.49 2.77-2.69.01-.03.01-.14-.07-.18-.08-.04-.19-.01-.27-.01-.12.02-1.92 1.24-5.43 3.61-.51.35-.98.52-1.4.51-.46-.01-1.35-.26-2.01-.48-.81-.27-1.45-.42-1.39-.88.03-.24.45-.48 1.27-.74 4.96-2.16 8.27-3.59 9.93-4.29.68-.29 1.42-.34 1.91-.34.11 0 .35.02.51.05.16.03.35.11.46.25.11.14.14.32.12.48z" />
                            </svg>
                            {locale === "ru"
                              ? "Telegram"
                              : locale === "cs"
                              ? "Telegram"
                              : "Telegram"}
                          </a>
                        </div>

                        {canShare && (
                          <button
                            onClick={async () => {
                              try {
                                await navigator.share({
                                  title:
                                    locale === "ru"
                                      ? "Счет-фактура"
                                      : locale === "cs"
                                      ? "Faktura"
                                      : "Invoice",
                                  text: `${
                                    locale === "ru"
                                      ? "Пароль"
                                      : locale === "cs"
                                      ? "Heslo"
                                      : "Password"
                                  }: ${sharePassword}`,
                                  url: generatedShareUrl || "",
                                });
                              } catch (err) {
                                console.error("Error sharing:", err);
                              }
                            }}
                            className="w-full py-2.5 sm:py-3 px-3 sm:px-4 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs transition-all flex items-center justify-center gap-2 hover:bg-slate-200 dark:hover:bg-slate-700"
                          >
                            <Share className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            {locale === "ru"
                              ? "Поделиться"
                              : locale === "cs"
                              ? "Sdílet"
                              : "Share"}
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showExportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
            onMouseDown={(e) =>
              setMouseDownOnOverlay(e.target === e.currentTarget)
            }
            onMouseUp={(e) => {
              if (mouseDownOnOverlay && e.target === e.currentTarget) {
                setShowExportModal(false);
              }
              setMouseDownOnOverlay(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-200 dark:border-slate-700"
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <TableIcon className="h-5 w-5 text-indigo-600" />
                    {t.statistics.exportModal.title}
                  </h3>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Date Range */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      {t.statistics.exportModal.selectRange}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">
                          {t.statistics.exportModal.from}
                        </span>
                        <input
                          type="date"
                          value={exportStartDate}
                          onChange={(e) => setExportStartDate(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">
                          {t.statistics.exportModal.to}
                        </span>
                        <input
                          type="date"
                          value={exportEndDate}
                          onChange={(e) => setExportEndDate(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Language Selector */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      {t.statistics.exportModal.tableLanguage}
                    </label>
                    <select
                      value={exportLang}
                      onChange={(e) => setExportLang(e.target.value as Locale)}
                      className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 0.75rem center",
                        backgroundSize: "1rem",
                        paddingRight: "2.5rem",
                      }}
                    >
                      <option value="cs">Čeština (CS)</option>
                      <option value="en">English (EN)</option>
                      <option value="ru">Русский (RU)</option>
                    </select>
                  </div>

                  {exportStartDate &&
                    exportEndDate &&
                    exportStartDate > exportEndDate && (
                      <p className="text-red-500 text-xs font-semibold mt-1">
                        {locale === "ru"
                          ? "Дата 'ОТ' не может быть позже 'ДО'"
                          : locale === "cs"
                          ? "Datum 'OD' nesmí být po 'DO'"
                          : "Start date cannot be later than end date"}
                      </p>
                    )}

                  <motion.button
                    whileHover={
                      isExporting || exportStartDate > exportEndDate
                        ? {}
                        : { scale: 1.02 }
                    }
                    whileTap={
                      isExporting || exportStartDate > exportEndDate
                        ? {}
                        : { scale: 0.98 }
                    }
                    onClick={handleExportToXlsx}
                    disabled={isExporting || exportStartDate > exportEndDate}
                    className="w-full py-3 rounded-xl bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-4 transition-all"
                  >
                    {isExporting ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      />
                    ) : (
                      <DownloadIcon className="h-4 w-4" />
                    )}
                    {t.statistics.exportModal.download}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showOnboarding && (
        <InvoiceListTour 
          onComplete={handleOnboardingComplete} 
          forceStart={localStorage.getItem(LS_INVOICE_LIST_ONBOARDING_COMPLETED) === "true"}
        />
      )}
    </div>
  );
}

// Icons
function SearchIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="m21 21-4.35-4.35"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CopyIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="9"
        y="9"
        width="13"
        height="13"
        rx="2"
        ry="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function TrashIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polyline
        points="3,6 5,6 21,6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DocumentIcon({ className = "h-12 w-12" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="14,2 14,8 20,8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="16"
        y1="13"
        x2="8"
        y2="13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="16"
        y1="17"
        x2="8"
        y2="17"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="10,9 9,9 8,9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 18l6-6-6-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDownIcon({ className = "h-3 w-3" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TableIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="3"
        y="3"
        width="18"
        height="18"
        rx="2"
        ry="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <line
        x1="3"
        y1="9"
        x2="21"
        y2="9"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <line
        x1="3"
        y1="15"
        x2="21"
        y2="15"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <line
        x1="9"
        y1="3"
        x2="9"
        y2="21"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <line
        x1="15"
        y1="3"
        x2="15"
        y2="21"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function DownloadIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="7,10 12,15 17,10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="15"
        x2="12"
        y2="3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
